import {remCalc} from '_themeUtils'

const sizeGuide = {
    SizeGuide: {
        baseStyle: () => ({
            // ─── SizeGuide (drawer body) ───────────────────────────────────
            container: {
                alignItems: 'flex-start',
                w: '100%',
            },
            tabsContainer: {
                flexDirection: 'column',
                w: '100%',
            },
            helpCtasContainer: {
                w: '100%',
                pt: 'spacing.10',
                gap: 'spacing.3',
                flexWrap: {base: 'wrap', md: 'nowrap'},
            },
            helpCta: {
                flex: 1,
                w: 'auto',
                minW: 0,
            },
            helpSizesCta: {},

            // ─── SizeGuideTab (accordion of sections) ──────────────────────
            accordionWrapper: {
                w: '100%',
                display: 'flex',
                flexDirection: 'column',
            },
            sectionContent: {
                pt: 'spacing.5',
                pb: 'spacing.10',
                bg: 'color.grey.200',
                // The Chakra AccordionPanel adds its default 16px (spacing.4) horizontal padding, which the base
                // Accordion theme doesn't reset. Cancel it with a negative margin and add no inset, so the table
                // columns start flush with the accordion block edge (aligned with "Size Guide" / the grey block).
                mx: '-spacing.4',
                px: 0,
            },
            firstSectionWrapper: {
                alignItems: 'center',
                flexDirection: {base: 'column-reverse', md: 'row'},
            },

            // ─── SizeGuideTable ─────────────────────────────────────────────
            tableSwitchWrapper: {
                w: '100%',
                justifyContent: 'flex-end',
                mb: 'spacing.5',
                gap: 0,
            },
            tableSwitchButton: {
                minW: remCalc(34),
                h: remCalc(34),
                p: 0,
                textStyle: 'paragraphXxs',
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
            },
            tableImageWrapper: {
                w: {base: '80%', md: '50%'},
                mb: 'spacing.5',
            },
            tabDescription: {
                w: {base: '100%', md: '50%'},
                textStyle: 'paragraphSm',
                pl: {base: 0, md: remCalc(40)},
            },
            headerNotes: {
                textStyle: 'paragraphXs',
                color: 'grey.600',
                mb: 'spacing.5',
            },
            tableWrapper: {
                w: '100%',
                overflowX: 'auto',
            },
            table: {
                w: '100%',
                borderCollapse: 'collapse',
                // Figma: columns are equal width (flex-[1_0_0]); fixed layout distributes them evenly and lets
                // column titles wrap to a second line instead of widening the column.
                tableLayout: 'fixed',
            },
            tableHeaderRow: {
                borderBottom: '1px solid',
                borderColor: 'grey.300',
            },
            tableHeaderNation: {
                display: 'block',
                width: remCalc(24),
                height: remCalc(24),
                borderRadius: '30px',
                overflow: 'hidden',
                fontWeight: 500,
                color: 'white',
                fontSize: remCalc(13),
                lineHeight: remCalc(24),
                textAlign: 'center',
                margin: 0,
                background: 'teal.300',
            },
            tableHeaderNationDesc: {
                display: 'block',
                textStyle: 'paragraphXs',
                fontWeight: 350,
                textAlign: 'left',
                textTransform: 'capitalize',
                // Figma: 5px gap between the letter badge and the column title.
                paddingTop: remCalc(5),
                // Columns are fixed/equal width, so titles wrap to a second line when they don't fit.
                whiteSpace: 'normal',
            },
            tableHeaderNationDescTitle: {
                fontWeight: 400,
            },
            tableHeaderCell: {
                fontFamily: 'body',
                textStyle: 'paragraphXs',
                letterSpacing: 'normal',
                textTransform: 'none',
                fontWeight: 'light',
                color: 'black',
                textAlign: 'left',
                verticalAlign: 'top',
                // Figma (5655-4572 / 5847-10424): every column shares a 15px horizontal inset (row px-15) so
                // values and column titles line up; the header row is p-15 vertically.
                py: remCalc(15),
                px: remCalc(15),
            },
            tableRow: {
                position: 'relative',
                borderBottom: '1px solid',
                borderColor: 'grey.300',
            },
            tableCell: {
                position: 'relative',
                textAlign: 'left',
                textStyle: 'paragraphMd',
                textTransform: 'none',
                color: 'grey.600',
                // Figma: data rows are px-15 / py-10; the 15px matches the header so columns align.
                py: remCalc(10),
                px: remCalc(15),
            },
            tableCellTitle: {
                color: 'black',
                fontWeight: 500,
            },
            footerNotes: {
                textStyle: 'paragraphXs',
                color: 'grey.600',
                mt: 'spacing.8',
            },

            // ─── MeasurementsTab ────────────────────────────────────────────
            measurementsSection: {
                w: '100%',
                pt: 'spacing.3',
            },
            sectionTitle: {
                textStyle: 'paragraphXl',
                fontWeight: 500,
                letterSpacing: '1px',
                color: 'black',
                mb: 'spacing.8',
            },
            measurementsTitle: {
                textStyle: 'paragraphLg',
                fontWeight: 500,
                letterSpacing: '1px',
                color: 'black',
                mb: 'spacing.6',
            },
            measurementsText: {
                textStyle: 'paragraphMd',
                fontWeight: 300,
                color: 'black',
                mb: 'spacing.10',
            },
            measurementsDetail: {
                w: '100%',
                flexDirection: {base: 'column', '2xl': 'row'},
                pb: remCalc(40),
                pl: {base: 0, '2xl': remCalc(20)},
            },
            measurementsImageWrapper: {
                w: {base: '100%'},
                alignItems: 'flex-start',
            },
            measurementsContentText: {
                w: {base: '100%'},
                mt: {base: 'spacing.8', '2xl': 0},
            },
            measurementsContentItem: {
                mt: {base: 'spacing.5', '2xl': remCalc(45)},
                _first: {mt: 0},
            },
            measurementsItemTitle: {
                textStyle: 'paragraphMd',
                fontWeight: 700,
                color: 'black',
                textTransform: 'uppercase',
                mb: 'spacing.2',
            },
            measurementsItemText: {
                textStyle: 'paragraphSm',
                color: 'grey.600',
            },
        }),
    },
}

export default sizeGuide
